import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase.js';
import { today } from '../../lib/dates.js';
import { useDailyCheckout } from '../../hooks/useDailyCheckout.js';
import StepMITReview from './StepMITReview.jsx';
import StepCarryForward from './StepCarryForward.jsx';
import StepWin from './StepWin.jsx';
import StepBrainDump from './StepBrainDump.jsx';

// Modal overlay with 4 sequential steps. Persists at each step. On finish,
// writes daily_wins + inbox + sets daily_checkout.completed_at, then closes.
export default function CheckOutWizard({ onClose }) {
  const { briefing, checkout, update, loading } = useDailyCheckout();
  const [step, setStep] = useState(1);

  // Close on Escape
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function finishCheckout(brainDumpText) {
    // 1. Write the daily_win row if there's text
    if (checkout?.daily_win) {
      await supabase
        .from('daily_wins')
        .insert({ date: today(), win: checkout.daily_win })
        .select();
      // Best-effort — we don't surface errors here; the field stays on
      // daily_checkout so nothing is lost.
    }

    // 2. Write the brain dump to inbox if non-empty
    if (brainDumpText && brainDumpText.trim()) {
      await supabase.from('inbox').insert({
        content: brainDumpText.trim(),
        source: 'dashboard',
        status: 'new',
      });
    }

    // 3. Mark checkout complete
    await update({ completed_at: new Date().toISOString() });

    onClose();
  }

  let body;
  if (loading) {
    body = <div className="text-text-muted text-center py-12">Loading…</div>;
  } else if (!briefing) {
    body = (
      <div className="max-w-md mx-auto bg-bg-card border border-border-base rounded p-6 text-center">
        <h2 className="text-base font-semibold text-text-primary mb-2">
          No briefing for today
        </h2>
        <p className="text-sm text-text-secondary mb-4">
          The check-out reviews today's morning MITs. Complete the morning
          briefing first.
        </p>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-accent-blue text-white text-sm font-medium rounded"
        >
          Close
        </button>
      </div>
    );
  } else {
    switch (step) {
      case 1:
        body = (
          <StepMITReview
            briefing={briefing}
            checkout={checkout}
            onUpdate={update}
            onNext={() => setStep(2)}
          />
        );
        break;
      case 2:
        body = (
          <StepCarryForward
            briefing={briefing}
            checkout={checkout}
            onUpdate={update}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        );
        break;
      case 3:
        body = (
          <StepWin
            checkout={checkout}
            onUpdate={update}
            onBack={() => setStep(2)}
            onNext={() => setStep(4)}
          />
        );
        break;
      case 4:
      default:
        body = (
          <StepBrainDump
            checkout={checkout}
            onUpdate={update}
            onBack={() => setStep(3)}
            onFinish={finishCheckout}
          />
        );
        break;
    }
  }

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/60 z-40 animate-fade-in"
      />
      <div className="fixed inset-0 z-50 overflow-y-auto px-4 py-8">
        <div className="relative">
          <button
            onClick={onClose}
            className="absolute top-0 right-2 text-text-secondary hover:text-text-primary text-sm"
            aria-label="Close"
          >
            ✕ Close
          </button>
          <div className="pt-8">{body}</div>
        </div>
      </div>
    </>
  );
}
